import { useEffect, useMemo, useRef, useState } from "react";
import FilterChips from "./shared/FilterChips";
import SearchInput from "./shared/SearchInput";
import { sendComplianceExpiryEmail } from "../utils/api";
import { cssUrl } from "../utils/cssVersion";

// FilterChips / SearchInput use the fr-* classes from this stylesheet —
// UsersManagement doesn't load it, so the modal loads it while mounted.
const filterTemplateStyles = cssUrl("filterTemplate.css");

/* ============================================================================
 * ComplianceExpiryModal
 * ----------------------------------------------------------------------------
 * Shows 6 buttons — Police / Visa / Children, each in "3 Months Expiry"
 * (approaching, cyan total) and "Expired" (red total) flavours.
 * Clicking a button renders a filtered table; clicking a row hands the full
 * record back to UsersManagement via onSelect so the admin can edit it.
 *
 * Scope chips (one from EACH group always selected):
 *   blue  – Working / Not Working   (record.working)
 *   green – Active / Non Active     (record.current)
 * Defaults: Working + Active.
 *
 * Props:
 *   records  – the full array from fetchManagedUsers (UsersManagement state)
 *   onSelect – (record) => void  — select the user in UsersManagement
 *   onClose  – () => void
 * ==========================================================================*/

/* ---------- category metadata ---------- */
const CATEGORIES = [
  { key: "police",   label: "Police",   priorField: "police3MonthsPrior",   expiryField: "policeExpiry" },
  { key: "visa",     label: "Visa",     priorField: "visa3MonthsPrior",     expiryField: "visaExpiry" },
  { key: "children", label: "Children", priorField: "children3MonthsPrior", expiryField: "workChildrenExpiry" },
];

const BUTTON_COLS = 3; // buttons per row (matches CATEGORIES length)

const STATUS_DISPLAY = { ABN: "Contractor", TFN: "Employee" };
const roleDisplay = (ws) => STATUS_DISPLAY[ws] ?? ws ?? "—";

/* ---------- chip groups (single-select within each group) ---------- */
const CHIP_GROUPS = [
  { color: "#3b82f6", values: ["Working", "Not Working"] }, // blue
  { color: "#22c55e", values: ["Active", "Non Active"] },   // green
];
const WORKING_TERMS = ["working", "not working"];
const CURRENT_TERMS = ["active", "non active"];
const DEFAULT_CHIP_QUERY = "working,active";

/* ---------- date helpers (accepts "YYYY-MM-DD..." or "DD/MM/YYYY") ---------- */
const parseAnyDate = (v) => {
  const s = (v ?? "").toString().trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
};

const displayDate = (v) => {
  const d = v instanceof Date ? v : parseAnyDate(v);
  if (!d) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// date +/- months (e.g. addMonths(today, 3) = the "3 months from now" cutoff,
// addMonths(expiry, -3) = the "3 months prior" warning date)
const addMonths = (date, months) => {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
};

/* ---------- styles (mirrors EmployeePayments) ---------- */
const S = {
  backdrop: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(2,6,23,0.7)", zIndex: 1100,
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 16px", overflowY: "auto",
  },
  modal: {
    position: "relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width: "100%", maxWidth: 880,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    padding: "18px 18px 14px",
  },
  panelAccent: {
    position: "absolute", top: 0, left: 0, right: 0, height: 3,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)",
  },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9" },
  closeX: {
    background: "none", border: "none", color: "rgba(255,255,255,0.6)",
    fontSize: "1.4rem", lineHeight: 1, cursor: "pointer",
  },
  eyebrow: {
    fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase",
    color: "#22d3ee", fontWeight: 700,
  },
  buttonsGrid: {
    display: "grid", gridTemplateColumns: `repeat(${BUTTON_COLS}, minmax(0, 1fr))`,
    gap: 10, marginBottom: 8,
  },
  countBtn: (active) => ({
    borderRadius: 12, padding: "12px 10px", cursor: "pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border: `1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(34,211,238,0.25)"}`,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    transition: "background .12s ease, border-color .12s ease",
  }),
  countLabel: {
    fontSize: "0.68rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase",
    letterSpacing: "0.05em", fontWeight: 700, textAlign: "center", lineHeight: 1.3,
  },
  countValue: (expired) => ({
    fontSize: "1.7rem", fontWeight: 800, lineHeight: 1,
    color: expired ? "#f87171" : "#22d3ee",
  }),
  card: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "12px", marginTop: 14,
  },
  colHead: {
    fontSize: "0.62rem", letterSpacing: "0.05em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowName: { fontSize: "0.85rem", fontWeight: 700, color: "#22d3ee", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cell: { fontSize: "0.84rem", color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  badge: (paidLike) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: 999,
    fontSize: "0.72rem", fontWeight: 700,
    ...(paidLike
      ? { background: "rgba(34,197,94,0.15)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.4)" }
      : { background: "rgba(251,191,36,0.15)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.4)" }),
  }),
  expiryCell: (expired) => ({
    fontSize: "0.84rem", fontWeight: 700, textAlign: "right",
    color: expired ? "#f87171" : "#e2e8f0", whiteSpace: "nowrap",
  }),
  empty: {
    padding: "24px", textAlign: "center", color: "rgba(255,255,255,0.5)",
    fontStyle: "italic", fontSize: "0.85rem",
  },
  hint: {
    marginTop: 10, fontSize: "0.72rem", color: "rgba(255,255,255,0.4)",
    textAlign: "center",
  },
  closeBtn: {
    width: "100%", padding: "11px", borderRadius: 10, border: "none",
    background: "rgba(255,255,255,0.06)", color: "#e2e8f0", fontWeight: 600,
    cursor: "pointer", marginTop: 12,
  },
};

const TABLE_COLS = "1.5fr 0.9fr 0.9fr minmax(0,1fr) minmax(0,1fr)";

/* ============================================================================
 * ExpiryEmailModal — nested above ComplianceExpiryModal (zIndex 1200)
 * Same visual language as SendPayrolEmails: confirm modal -> animated
 * sending card with progress -> delivery log. Sends one email per record,
 * sequentially, via sendComplianceExpiryEmail.
 * ==========================================================================*/

const CATEGORY_EMAIL_LABEL = {
  police: "Police check",
  visa: "Visa",
  children: "Working with Children check",
};

const previewMessage = (categoryKey, bucket, name = "{Name}") => {
  const label = CATEGORY_EMAIL_LABEL[categoryKey] ?? "document";
  const phrase =
    bucket === "expired" ? "has now expired" : "will expire in 3 Months";
  return `Hello ${name}, your ${label} ${phrase} from SNL Commerical Property Services PTY LTD, update immediately.`;
};

const E = {
  overlay: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(6px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 1200, padding: 20,
  },
  modal: {
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    width: "100%", maxWidth: 520, overflow: "hidden",
    boxShadow: "0 25px 80px rgba(0,0,0,0.6)",
  },
  gradientBar: { height: 3, background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)" },
  header: { padding: "20px 24px 8px", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  headTitle: { fontSize: "1.05rem", fontWeight: 700, color: "#f1f5f9", margin: 0 },
  body: { padding: "16px 24px", color: "rgba(255,255,255,0.75)", fontSize: "0.88rem", lineHeight: 1.5 },
  previewBox: {
    marginTop: 12, padding: "10px 12px", borderRadius: 10,
    background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.2)",
    fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", fontStyle: "italic",
  },
  footer: {
    padding: "16px 24px", display: "flex", justifyContent: "flex-end", gap: 10,
    background: "rgba(0,0,0,0.2)", borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  cancelBtn: {
    background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10,
    padding: "10px 18px", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
  },
  confirmBtn: {
    background: "linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)", color: "#0f172a",
    border: "none", borderRadius: 10, padding: "10px 22px", fontSize: "0.85rem",
    fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,211,238,0.3)",
  },
  /* sending card */
  sendingWrap: { padding: 24, textAlign: "center", position: "relative", overflow: "hidden" },
  pulse: {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    width: 200, height: 200, borderRadius: "50%",
    background: "radial-gradient(circle, rgba(34,211,238,0.2) 0%, transparent 70%)",
    animation: "cePulse 2s ease-in-out infinite", pointerEvents: "none",
  },
  sendingIcon: { fontSize: "3rem", marginBottom: 16, display: "inline-block", animation: "ceBounce 1.4s ease-in-out infinite" },
  sendingTitle: { fontSize: "1.1rem", fontWeight: 700, color: "#f1f5f9", marginBottom: 8, position: "relative" },
  sendingName: {
    fontSize: "1.3rem", fontWeight: 800,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6)",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    backgroundClip: "text", marginBottom: 14, letterSpacing: "-0.02em", position: "relative",
  },
  sendingRemaining: { fontSize: "0.85rem", color: "rgba(255,255,255,0.6)", marginBottom: 18, position: "relative" },
  progressTrack: {
    background: "rgba(255,255,255,0.06)", borderRadius: 9999, height: 8,
    overflow: "hidden", margin: "0 auto", maxWidth: 380, position: "relative",
  },
  progressFill: (pct) => ({
    height: "100%", width: `${pct}%`,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)",
    transition: "width 0.5s ease", borderRadius: 9999,
  }),
  progressCount: { fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", marginTop: 8, position: "relative" },
  /* log */
  logWrap: { padding: "14px 20px 6px" },
  logHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  logTitle: { fontSize: "0.85rem", fontWeight: 700, color: "#f1f5f9" },
  logList: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 240, overflowY: "auto" },
  logRow: (success) => ({
    display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
    background: success ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
    border: `1px solid ${success ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.25)"}`,
    borderRadius: 8, fontSize: "0.82rem",
  }),
  logIcon: (success) => ({ color: success ? "#10b981" : "#ef4444", fontSize: "1rem", fontWeight: 700 }),
};

const ExpiryEmailModal = ({ rows, categoryKey, bucket, listLabel, onClose }) => {
  const [phase, setPhase] = useState("confirm"); // 'confirm' | 'sending' | 'done'
  const [currentName, setCurrentName] = useState("");
  const [log, setLog] = useState([]); // [{ name, success, error? }]
  const sendingRef = useRef(false);

  const total = rows.length;
  const processed = log.length;
  const sent = log.filter((l) => l.success).length;
  const failed = processed - sent;
  const pct = total > 0 ? (processed / total) * 100 : 0;

  /* keyframes (same animation as SendPayrolEmails, ce-prefixed) */
  useEffect(() => {
    const id = "ce-email-keyframes";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      @keyframes cePulse {
        0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
        50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
      }
      @keyframes ceBounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const startSending = async () => {
    if (sendingRef.current || total === 0) return;
    sendingRef.current = true;
    setPhase("sending");
    setLog([]);
    for (const r of rows) {
      setCurrentName(r.name || "—");
      // eslint-disable-next-line no-await-in-loop
      const res = await sendComplianceExpiryEmail({
        recordId: r.recordId,
        category: categoryKey,
        bucket,
      });
      if (res.status === "success") {
        setLog((l) => [...l, { name: r.name || "—", success: true }]);
      } else {
        setLog((l) => [...l, { name: r.name || "—", success: false, error: res.error || "Send failed" }]);
      }
    }
    sendingRef.current = false;
    setPhase("done");
  };

  const canClose = phase !== "sending";

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && canClose && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [canClose, onClose]);

  return (
    <div
      style={E.overlay}
      onClick={(e) => {
        e.stopPropagation(); // don't let clicks reach the parent modal's backdrop
        if (e.target === e.currentTarget && canClose) onClose();
      }}
    >
      <div style={E.modal}>
        <div style={E.gradientBar} />

        {phase === "confirm" && (
          <>
            <div style={E.header}>
              <h5 style={E.headTitle}>Send expiry emails?</h5>
            </div>
            <div style={E.body}>
              You&apos;re about to send{" "}
              <strong style={{ color: "#22d3ee" }}>{total}</strong> email
              {total !== 1 ? "s" : ""} for{" "}
              <strong style={{ color: "#f1f5f9" }}>{listLabel}</strong> from{" "}
              <strong style={{ color: "#f1f5f9" }}>SNL Commercial Property Services</strong>.
              <div style={E.previewBox}>“{previewMessage(categoryKey, bucket)}”</div>
            </div>
            <div style={E.footer}>
              <button type="button" style={E.cancelBtn} onClick={onClose}>
                Cancel
              </button>
              <button type="button" style={E.confirmBtn} onClick={startSending} disabled={total === 0}>
                Send {total} email{total !== 1 ? "s" : ""}
              </button>
            </div>
          </>
        )}

        {phase === "sending" && (
          <div style={E.sendingWrap}>
            <div style={E.pulse} />
            <div style={E.sendingIcon}>✉️</div>
            <div style={E.sendingTitle}>Sending email to</div>
            <div style={E.sendingName}>{currentName || "…"}</div>
            <div style={E.sendingRemaining}>
              {total - processed > 0
                ? `${total - processed} email${total - processed !== 1 ? "s" : ""} left to send`
                : "Finishing up…"}
            </div>
            <div style={E.progressTrack}>
              <div style={E.progressFill(pct)} />
            </div>
            <div style={E.progressCount}>
              {processed} of {total} processed ({Math.round(pct)}%)
            </div>
          </div>
        )}

        {phase === "done" && (
          <>
            <div style={E.logWrap}>
              <div style={E.logHead}>
                <div style={E.logTitle}>
                  Delivery Log — {sent} sent{failed > 0 && `, ${failed} failed`}
                </div>
              </div>
              <div style={E.logList}>
                {log.map((entry, i) => (
                  <div key={i} style={E.logRow(entry.success)}>
                    <span style={E.logIcon(entry.success)}>{entry.success ? "✓" : "✗"}</span>
                    <span style={{ color: "#f1f5f9", fontWeight: 600 }}>{entry.name}</span>
                    {!entry.success && entry.error && (
                      <span style={{ color: "#fca5a5", fontSize: "0.75rem", marginLeft: "auto" }}>
                        {entry.error}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div style={E.footer}>
              <button type="button" style={E.confirmBtn} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ---------- component ---------- */
const ComplianceExpiryModal = ({ records, onSelect, onClose }) => {
  // selection: { categoryKey, bucket: 'soon'|'expired' }
  const [selection, setSelection] = useState(null);
  // Scope chips — one from each group, defaults Working + Active
  // (matches the clock badge in UsersManagement)
  const [chipQuery, setChipQuery] = useState(DEFAULT_CHIP_QUERY);
  const [chipsOpen, setChipsOpen] = useState(false);
  // typed name filter — applies to the table rows
  const [nameQuery, setNameQuery] = useState("");
  // nested email modal
  const [emailOpen, setEmailOpen] = useState(false);

  /* Each chip group is mutually exclusive (radio behaviour) and always has
   * exactly one selection. FilterChips appends a newly-clicked chip last,
   * so the term that wasn't previously active is the new pick; it replaces
   * its own group's selection and the other group's pick is kept. Clicking
   * the already-active chip (a deselect) is refused. */
  const handleChipQuery = (next) => {
    const nextTerms = (next || "")
      .toLowerCase()
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const prevTerms = chipQuery
      .toLowerCase()
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const added = nextTerms.find((t) => !prevTerms.includes(t));
    if (!added) return; // deselect attempt — every group keeps one selection

    const keepWorking = WORKING_TERMS.includes(added)
      ? added
      : prevTerms.find((t) => WORKING_TERMS.includes(t)) ?? "working";
    const keepCurrent = CURRENT_TERMS.includes(added)
      ? added
      : prevTerms.find((t) => CURRENT_TERMS.includes(t)) ?? "active";

    setChipQuery(`${keepWorking},${keepCurrent}`);
  };

  /* Load filterTemplate.css while the modal is open (fr-chip / fr-search-*) */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && !emailOpen && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, emailOpen]);

  /* Chip scope — record.working: "Working" or anything-else = Not Working;
   * record.current: "Active" or anything-else = Non Active. A record must
   * match BOTH selected chips. */
  const chipTerms = useMemo(
    () =>
      chipQuery
        .toLowerCase()
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [chipQuery]
  );

  const scopedRecords = useMemo(() => {
    const workingTerm =
      chipTerms.find((t) => WORKING_TERMS.includes(t)) ?? "working";
    const currentTerm =
      chipTerms.find((t) => CURRENT_TERMS.includes(t)) ?? "active";
    return (records || []).filter((r) => {
      const w = r.working === "Working" ? "working" : "not working";
      const c = r.current === "Active" ? "active" : "non active";
      return w === workingTerm && c === currentTerm;
    });
  }, [records, chipTerms]);

  /* Bucket every record once per open.
   * The *3MonthsPrior fields are just (expiry - 3 months), so the expiry
   * date is the single source of truth:
   *   expired : expiry < today
   *   soon    : today <= expiry <= today + 3 months
   */
  const buckets = useMemo(() => {
    const today = startOfToday();
    const soonCutoff = addMonths(today, 3);
    const out = {};
    CATEGORIES.forEach((cat) => {
      const soon = [];
      const expired = [];
      scopedRecords.forEach((r) => {
        const expiry = parseAnyDate(r[cat.expiryField]);
        if (!expiry) return;
        if (expiry < today) {
          expired.push(r);
        } else if (expiry <= soonCutoff) {
          soon.push(r);
        }
      });
      const byExpiry = (a, b) =>
        (parseAnyDate(a[cat.expiryField])?.getTime() ?? 0) -
        (parseAnyDate(b[cat.expiryField])?.getTime() ?? 0);
      soon.sort(byExpiry);
      expired.sort(byExpiry);
      out[cat.key] = { soon, expired };
    });
    return out;
  }, [scopedRecords]);

  const activeCategory = selection
    ? CATEGORIES.find((c) => c.key === selection.categoryKey)
    : null;

  const bucketRows = selection
    ? buckets[selection.categoryKey][selection.bucket]
    : [];

  // Name search only narrows the table — button totals stay chip-driven
  const activeRows = useMemo(() => {
    const q = nameQuery.toLowerCase().replace(/\s+/g, "");
    if (!q) return bucketRows;
    return bucketRows.filter((r) =>
      (r.name ?? "").toLowerCase().replace(/\s+/g, "").includes(q)
    );
  }, [bucketRows, nameQuery]);

  const toggleSelection = (categoryKey, bucket) =>
    setSelection((prev) =>
      prev && prev.categoryKey === categoryKey && prev.bucket === bucket
        ? null
        : { categoryKey, bucket }
    );

  const isActive = (categoryKey, bucket) =>
    selection?.categoryKey === categoryKey && selection?.bucket === bucket;

  const renderCountButton = (cat, bucket) => {
    const expired = bucket === "expired";
    const count = buckets[cat.key][bucket].length;
    const label = expired
      ? `${cat.label} Expired`
      : `${cat.label} 3 Months Expiry`;
    return (
      <button
        key={`${cat.key}:${bucket}`}
        type="button"
        onClick={() => toggleSelection(cat.key, bucket)}
        style={S.countBtn(isActive(cat.key, bucket))}
        title={
          expired
            ? `${cat.label} records already expired`
            : `${cat.label} records expiring within 3 months`
        }
      >
        <span style={S.countLabel}>{label}</span>
        <span style={S.countValue(expired)}>{count}</span>
      </button>
    );
  };

  return (
    <div style={S.backdrop} onClick={onClose}>
      <style>{`
        .ce-row { transition: background .12s ease; }
        .ce-row:hover { background: rgba(34,211,238,0.07); }
      `}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />

        <div style={S.head}>
          <h4 style={S.title}>Compliance Expiry&apos;s</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* Scope chips (blue Working group + green Active group) + Send Total Emails */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <FilterChips
              chipGroups={CHIP_GROUPS}
              chipQuery={chipQuery}
              setChipQuery={handleChipQuery}
              chipsOpen={chipsOpen}
              setChipsOpen={setChipsOpen}
            />
          </div>

          {selection && activeCategory && (
            <button
              type="button"
              onClick={() => activeRows.length > 0 && setEmailOpen(true)}
              disabled={activeRows.length === 0}
              title={
                activeRows.length === 0
                  ? "No records in this list to email"
                  : `Email all ${activeRows.length} people in this list`
              }
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                padding: "8px 18px",
                borderRadius: 999,
                flexShrink: 0,
                cursor: activeRows.length === 0 ? "not-allowed" : "pointer",
                opacity: activeRows.length === 0 ? 0.4 : 1,
                background:
                  selection.bucket === "expired"
                    ? "rgba(248,113,113,0.12)"
                    : "rgba(34,211,238,0.12)",
                border: `1px solid ${
                  selection.bucket === "expired"
                    ? "rgba(248,113,113,0.5)"
                    : "rgba(34,211,238,0.5)"
                }`,
                color: selection.bucket === "expired" ? "#f87171" : "#22d3ee",
                boxShadow:
                  selection.bucket === "expired"
                    ? "0 0 8px rgba(248,113,113,0.2)"
                    : "0 0 8px rgba(34,211,238,0.2)",
              }}
            >
              <span style={{ fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                Send Total Emails
              </span>
              <span
                style={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  opacity: 0.85,
                  whiteSpace: "nowrap",
                }}
              >
                {activeCategory.label}{" "}
                {selection.bucket === "expired" ? "Expired" : "3 Months Expiry"} ·{" "}
                {activeRows.length}
              </span>
            </button>
          )}
        </div>

        {/* Name search — narrows the table rows below */}
        <div style={{ margin: "8px 0 14px" }}>
          <SearchInput
            value={nameQuery}
            onChange={setNameQuery}
            placeholder="Search name in results…"
          />
        </div>

        {/* Row 1 — approaching (3 months prior reached, not yet expired) */}
        <div style={{ ...S.eyebrow, margin: "0 2px 8px" }}>Expiring within 3 months</div>
        <div style={S.buttonsGrid}>
          {CATEGORIES.map((cat) => renderCountButton(cat, "soon"))}
        </div>

        {/* Row 2 — already expired (red totals) */}
        <div style={{ ...S.eyebrow, margin: "8px 2px 8px", color: "#f87171" }}>
          Already expired
        </div>
        <div style={S.buttonsGrid}>
          {CATEGORIES.map((cat) => renderCountButton(cat, "expired"))}
        </div>

        {/* Filtered table */}
        {selection && activeCategory && (
          <div style={S.card}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  ...S.eyebrow,
                  color: selection.bucket === "expired" ? "#f87171" : "#22d3ee",
                }}
              >
                {activeCategory.label}{" "}
                {selection.bucket === "expired" ? "— expired" : "— expiring within 3 months"}
              </span>
              <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)" }}>
                {nameQuery
                  ? `${activeRows.length} of ${bucketRows.length}`
                  : activeRows.length}{" "}
                record{activeRows.length === 1 ? "" : "s"}
              </span>
            </div>

            {activeRows.length === 0 ? (
              <div style={S.empty}>
                {nameQuery
                  ? `No names matching “${nameQuery}” in this list.`
                  : "No matching records."}
              </div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: "auto", overflowX: "auto" }}>
                <div style={{ minWidth: 620 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: TABLE_COLS,
                      alignItems: "end",
                      padding: "8px 8px 10px",
                      borderBottom: "1px solid rgba(255,255,255,0.12)",
                      columnGap: 8,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      background: "#1b2537",
                    }}
                  >
                    <span style={S.colHead}>Name</span>
                    <span style={S.colHead}>Status</span>
                    <span style={S.colHead}>Working</span>
                    <span style={{ ...S.colHead, textAlign: "right" }}>3 Months Prior</span>
                    <span style={{ ...S.colHead, textAlign: "right" }}>Expiry</span>
                  </div>

                  {activeRows.map((r, i) => (
                    <div
                      key={r.recordId || i}
                      className="ce-row"
                      onClick={() => onSelect(r)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: TABLE_COLS,
                        alignItems: "center",
                        padding: "10px 8px",
                        cursor: "pointer",
                        borderRadius: 8,
                        borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                        columnGap: 8,
                      }}
                      title={`Open ${r.name || "record"} in User Management`}
                    >
                      <span style={S.rowName}>{r.name || "—"}</span>
                      <span style={S.cell}>{roleDisplay(r.workstatus)}</span>
                      <span>
                        <span style={S.badge(r.working === "Working")}>
                          {r.working || "—"}
                        </span>
                      </span>
                      <span style={{ ...S.cell, textAlign: "right" }}>
                        {(() => {
                          // Airtable formula value if present, else expiry - 3 months
                          const prior = parseAnyDate(r[activeCategory.priorField]);
                          if (prior) return displayDate(r[activeCategory.priorField]);
                          const expiry = parseAnyDate(r[activeCategory.expiryField]);
                          return expiry ? displayDate(addMonths(expiry, -3)) : "—";
                        })()}
                      </span>
                      <span style={S.expiryCell(selection.bucket === "expired")}>
                        {displayDate(r[activeCategory.expiryField])}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={S.hint}>
              Click a record to open it in User Management for editing.
            </div>
          </div>
        )}

        <button type="button" style={S.closeBtn} onClick={onClose}>
          Close
        </button>
      </div>

      {/* Nested email modal — sits above this modal (zIndex 1200) */}
      {emailOpen && selection && activeCategory && (
        <ExpiryEmailModal
          rows={activeRows}
          categoryKey={selection.categoryKey}
          bucket={selection.bucket}
          listLabel={`${activeCategory.label} ${
            selection.bucket === "expired" ? "Expired" : "3 Months Expiry"
          }`}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </div>
  );
};

export default ComplianceExpiryModal;